import React, { useGlobal } from 'reactn'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import ApplicationEntry from 'app/medical/ApplicationEntry'
import NotFound from 'app/medical/NotFound'
import SystemError from 'app/medical/SystemError'
import Reception from 'app/personal/Reception'
import Reservation from 'app/personal/Reservation'
import Meeting from 'app/personal/Meeting'
import PersonalLegacyBrowser from 'app/personal/LegacyBrowser'
import MedLogin from 'app/medical/Login'
import MedReservations from 'app/medical/Reservations'
import MedPreReservations from 'app/medical/PreReservations'
import MedSetting from 'app/medical/Setting'
import MedAccountSetting from 'app/medical/AccountSetting'
import MedLegacyBrowser from './medical/LegacyBrowser'
import OAuth from 'app/medical/OAuth'
import AdminMedicalInstitutionList from 'app/admin/MedicalInstitutionList'
import AdminMedicalInstitutionEdit from 'app/admin/MedicalInstitutionEdit'
import GAHistoryListener from './GAHistoryListener'
import {
  eligibilityVerificationCallbackUrlPath,
  OnlineEligibilityVerificationCallbackScreen,
} from 'app/personal/OnlineEligibilityVerification'
import {
  eligibilityVerificationCanceledUrlPath,
  OnlineEligibilityVerificationCancel,
  OnlineEligibilityVerificationCancelCallbackScreen,
} from 'app/personal/OnlineEligibilityVerificationCancel'

import { UAParser } from 'ua-parser-js'
import Root from 'app/shared/Root'
import LangSwitch from 'ui/utils/LangSwitch'
import { useLanguage } from '../i18n'

// NOTE: 外部でも使うroute path
export const routePaths = {
  medical: '/medical',
  reservations: '/reservations',
  setting: '/setting',
  about: '/about',
  error: '/error',
  admin: '/agency/admin',
}

const uaParser = new UAParser()

const RoutesComponent: React.FC = () => {
  const { language, languages, setLanguage } = useLanguage()
  const location = useLocation()
  const showLang = !location.pathname.startsWith('/agency/admin')

  const [, setOffline] = useGlobal('offline')
  window.addEventListener('offline', () => {
    setOffline(true)
  })

  window.addEventListener('online', () => {
    setOffline(false)
  })

  const showLangComponent = showLang ? (
    <LangSwitch
      locale={language}
      locales={languages}
      onSelectLanguage={setLanguage}
    />
  ) : null

  // NOTE: 古いedgeのみエラー画面を表示
  if (uaParser.getEngine().name === 'EdgeHTML') {
    return (
      <>
        <Routes>
          {['/personal/*', '/reception/*'].map((path) => (
            <Route key={path} path={path} element={<PersonalLegacyBrowser />} />
          ))}
          <Route path="/*" element={<MedLegacyBrowser />} />
        </Routes>
        {showLangComponent}
      </>
    )
  }

  return (
    <>
      <GAHistoryListener />
      <Routes>
        <Route index element={<Root />} />
        <Route path="/error" element={<SystemError />} />
        {['/registration', '/agency'].map((path) => (
          <Route key={path} path={path} element={<ApplicationEntry />} />
        ))}
        {[
          '/reception/:institution_access_key',
          '/reception/:institution_access_key/:provisional_reservation_access_key',
        ].map((path) => (
          <Route key={path} path={path} element={<Reception />} />
        ))}
        <Route
          path={'/personal/reservation/:institution_access_key/:access_key'}
          element={<Reservation />}
        />
        <Route
          // NOTE: 資格確認完了後のコールバック画面
          path={eligibilityVerificationCallbackUrlPath}
          element={<OnlineEligibilityVerificationCallbackScreen />}
        />
        <Route
          // NOTE: 資格確認キャンセルに遷移するURLを生成する画面
          path={`${eligibilityVerificationCanceledUrlPath}/:institution_access_key/:access_key/:reservation_date`}
          element={<OnlineEligibilityVerificationCancel />}
        />
        <Route
          // NOTE: 資格確認キャンセル完了後のコールバック画面
          path={eligibilityVerificationCanceledUrlPath}
          element={<OnlineEligibilityVerificationCancelCallbackScreen />}
        />
        {[
          '/personal/meeting/:app/:institution_access_key/:access_key',
          '/personal/meeting/:institution_access_key/:access_key',
        ].map((path) => (
          <Route key={path} path={path} element={<Meeting />} />
        ))}
        {['/personal', '/reception'].map((path) => (
          <Route key={path} path={path} element={<NotFound />} />
        ))}
        {[
          '/stripe/authentication',
          '/zoom/authentication',
          '/teams/authentication',
        ].map((path) => (
          <Route key={path} path={path} element={<OAuth />} />
        ))}
        <Route
          path={routePaths.admin}
          element={<AdminMedicalInstitutionList />}
        />
        <Route
          path={`${routePaths.admin}/edit/:institution_id`}
          element={<AdminMedicalInstitutionEdit />}
        />
        <Route
          path={`${routePaths.medical}/*`}
          element={
            <MedLogin>
              <Routes>
                <Route
                  index
                  element={
                    <Navigate
                      to={`${routePaths.medical}${routePaths.reservations}`}
                    />
                  }
                />
                {[
                  `${routePaths.reservations}/pre`,
                  `${routePaths.reservations}/pre/new`,
                  `${routePaths.reservations}/pre/:pre_reservation_id`,
                ].map((path) => (
                  <Route
                    key={path}
                    path={path}
                    element={<MedPreReservations />}
                  />
                ))}
                {[
                  routePaths.reservations,
                  `${routePaths.reservations}/:date/:reservation_id`,
                  `${routePaths.reservations}/:date`,
                ].map((path) => (
                  <Route key={path} path={path} element={<MedReservations />} />
                ))}
                {[
                  routePaths.setting,
                  `${routePaths.setting}/department/:department_id`,
                ].map((path) => (
                  <Route key={path} path={path} element={<MedSetting />} />
                ))}
                <Route
                  path={routePaths.about}
                  element={<MedAccountSetting />}
                />
                <Route element={<NotFound />} />
              </Routes>
            </MedLogin>
          }
        />
      </Routes>
      {showLangComponent}
    </>
  )
}
export default RoutesComponent
